import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { formatDate } from '@/lib/format';
import { app_claim_path, app_export_path } from '@/rails/routes';
import { Form } from '@inertiajs/react';

interface HandoffTabProps {
  appId: string;
  exportAllowed: boolean;
  claim: { allowed: boolean; requestedAt: string | null };
  canManage: boolean;
}

const MAX_NOTE_LENGTH = 1000;

const HandoffTab = ({ appId, exportAllowed, claim, canManage }: HandoffTabProps) => (
  <div className="grid gap-6 lg:grid-cols-2">
    <Card>
      <CardHeader>
        <CardTitle>
          <h2>Export</h2>
        </CardTitle>
        <CardDescription>
          The design and its schema as one JSON file to keep in Git.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {exportAllowed ? (
          <Button asChild variant="outline">
            {/* A file download, so a plain link and not a page visit. */}
            <a href={app_export_path(appId)}>Download export</a>
          </Button>
        ) : (
          <p className="text-muted-foreground text-sm">Export is part of Studio.</p>
        )}
      </CardContent>
    </Card>

    <Card>
      <CardHeader>
        <CardTitle>
          <h2>Claim the project</h2>
        </CardTitle>
        <CardDescription>
          Take over this app&apos;s database and bucket. A person at CAP Rock Labs does the handoff.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ClaimState appId={appId} claim={claim} canManage={canManage} />
      </CardContent>
    </Card>
  </div>
);

type ClaimStateProps = Pick<HandoffTabProps, 'appId' | 'claim' | 'canManage'>;

const ClaimState = ({ appId, claim, canManage }: ClaimStateProps) => {
  if (!claim.allowed) {
    return <p className="text-muted-foreground text-sm">Claiming a project is part of Studio.</p>;
  }

  if (claim.requestedAt) {
    return (
      <p className="text-sm">
        Requested on {formatDate(claim.requestedAt)}. CAP Rock Labs will arrange it with you.
      </p>
    );
  }

  if (!canManage) {
    return <p className="text-muted-foreground text-sm">An owner or admin requests the handoff.</p>;
  }

  return (
    <Form action={app_claim_path(appId)} method="post" className="space-y-3">
      {({ processing }) => (
        <>
          <Label htmlFor="claim-note">Note (optional)</Label>
          <Textarea
            id="claim-note"
            name="note"
            rows={3}
            maxLength={MAX_NOTE_LENGTH}
            placeholder="Anything CAP Rock Labs should know before the handoff."
          />
          <Button type="submit" disabled={processing}>
            {processing ? 'Requesting…' : 'Request handoff'}
          </Button>
        </>
      )}
    </Form>
  );
};

export default HandoffTab;
